import { set } from 'lodash-es';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { createSchedulerFiltersStore, createSchedulerHighlightStore } from '@lp/core/store/scheduler';
import { SOrganGroup } from '@lp/features/Scheduler/SchedulerSurgeons/types';
import { createSelectors } from '@/utils/helpers';

export type OrganRegionToResourceIdMap = Record<SOrganGroup, Record<string, string[]>>;

type SScheduleState = {
  regionConfigs: Record<SOrganGroup, Record<string, boolean | undefined>>;
  assignedSurgeonsMap: OrganRegionToResourceIdMap;
};

type SScheduleActions = {
  setRegionExpand: (organGroup: SOrganGroup, regionId: string, expand?: boolean) => void;
  assignSurgeonsToRegion: (organGroup: SOrganGroup, regionId: string, userIds: string[]) => void;
};

type SScheduleStore = SScheduleState & SScheduleActions;

const useSSchedulerStoreBase = create<SScheduleStore>()(
  immer((setState) => ({
    regionConfigs: {
      [SOrganGroup.Liver]: {},
      [SOrganGroup.HeartAndLungs]: {},
    },
    assignedSurgeonsMap: {
      [SOrganGroup.Liver]: {},
      [SOrganGroup.HeartAndLungs]: {},
    },
    setRegionExpand: (organGroup, regionId, expand) => {
      setState((state) => {
        const newValue = expand === undefined ? !state.regionConfigs[organGroup][regionId] : expand;
        set(state.regionConfigs, [organGroup, regionId], newValue);
      });
    },
    assignSurgeonsToRegion: (organGroup, regionId, userIds) => {
      setState((state) => {
        const assignedIds = state.assignedSurgeonsMap[organGroup][regionId] ?? [];
        const newIds = Array.from(new Set([...assignedIds, ...userIds]));
        set(state.assignedSurgeonsMap, [organGroup, regionId], newIds);
      });
    },
  })),
);

const getRegionState = (organGroup: SOrganGroup, id: string) => (store: SScheduleStore) =>
  store.regionConfigs[organGroup][id];

export const sSchedulerSelectors = {
  getRegionState,
};

export const useSSchedulerStore = createSelectors(useSSchedulerStoreBase);

export const useLiSSchedulerFiltersStore = createSchedulerFiltersStore();
export const useHtLuSSchedulerFiltersStore = createSchedulerFiltersStore();

type SSchedulerHighlightValue = {
  organGroup: string;
  regionId: string;
  resourceId: string;
  day: Date;
};

export const useSSchedulerHighlightStore = createSchedulerHighlightStore<SSchedulerHighlightValue>();
